/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import InputLabel from '@mui/material/InputLabel';
import LinearProgress from '@mui/material/LinearProgress';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import PushPinIcon from '@mui/icons-material/PushPin';
import StraightenIcon from '@mui/icons-material/Straighten';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import PinDropIcon from '@mui/icons-material/PinDrop';

import { RadioButtons } from 'handimap-react-lib/components/PoiListingMenuComponents/RadioButtons';
import { FeatureListingResult } from 'handimap-react-lib/components/PoiListingMenuComponents/FeatureListingResult';
import { callPostSearchQuery } from 'handimap-react-lib/api/jsonFeatureApi';
import { GeocoderInput } from '../components/GeocoderInput';
import { hideAlert } from 'handimap-react-lib/store/actions/alertActions';
import { closePoiListingMenu } from 'handimap-react-lib/store/actions/poiListingActions';
import { GoBackMapButton } from 'handimap-react-lib/components/PoiListingMenuComponents/GoBackMapButton';

const TIME_OUT_THRESHOLD = 800; // ms
const distanceLabels = ['100 m', '500 m', '1 km', '2 km', '3 km'];
const distanceValues = [100, 500, 1000, 2000, 3000];

export const PoiListingMenu = ({ isMobile }) => {
  const { i18n, t } = useTranslation();
  const dispatch = useDispatch();

  const buttonsLayers = useSelector(
    (state) => state.layerConf.buttonsLayersState,
  );
  const queriableLayers = useSelector(
    (state) => state.layerConf.getFeatureInfoAttrs,
  );
  const showAlert = useSelector((state) => state.alert.isOpen);

  const [distance, setDistance] = useState(100);
  const [theme, setTheme] = useState(null);
  const [layersForTheme, setLayersForTheme] = useState(null);
  const [addressCoords, setAddressCoords] = useState([]);
  const [layerList, setLayerList] = useState([]);
  const [layerLabelList, setLayerLabelList] = useState([]);
  const [layerIconList, setLayerIconList] = useState([]);
  const [poiList, setPoiList] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);

  const handlePoiList = (layersList) => {
    if (addressCoords.length > 0 && theme !== null) {
      setIsLoading(true);
      callPostSearchQuery(
        layersList,
        addressCoords[0],
        addressCoords[1],
        distance,
      )
        .then((result) => {
          const { query, time } = result;
          if (TIME_OUT_THRESHOLD - time > 0) {
            /**
             * if time execution is shorter than 800 then setTimeout at 800
             */
            setTimeout(() => {
              query.then((poiResult) => {
                setPoiList(poiResult);
                setIsLoading(false);
              });
            }, TIME_OUT_THRESHOLD);
          } else {
            /**
             * if time execution is longer than 800 then set result immediately
             */
            query.then((poiResult) => {
              setPoiList(poiResult);
              setIsLoading(false);
            });
          }
        })
        .catch((error) => {
          console.error(error.message);
          setPoiList([]);
          setIsLoading(false);
        });
    }
  };

  const closePoiListing = () => {
    dispatch(closePoiListingMenu());
  };

  useEffect(() => {
    var layers = [];
    var labels = [];
    var icons = [];
    for (const [layer, obj] of Object.entries(buttonsLayers)) {
      layers.push(layer);
      labels.push(obj.label);
      icons.push(obj.blackPngIcon);
    }
    setLayerList(layers);
    setLayerLabelList(labels);
    setLayerIconList(icons);
    setTheme(layers[0]);
  }, [buttonsLayers]);

  useEffect(() => {
    if (theme !== null && theme !== undefined) {
      const layersForTheme = buttonsLayers[theme].layersName.filter((layer) => {
        if (Object.keys(queriableLayers).includes(layer)) {
          return true;
        } else {
          return false;
        }
      });
      handlePoiList(layersForTheme);
      setLayersForTheme(layersForTheme);
    }
  }, [theme]);

  useEffect(() => {
    if (showAlert) {
      dispatch(hideAlert());
    }
    handlePoiList(layersForTheme);
  }, [distance, addressCoords, i18n]);

  const styles = {
    container: {
      width: '100%',
      backgroundColor: '#E5E5E5',
      height: '100%',
    },
    subContainer: {
      backgroundColor: '#E5E5E5',
      display: 'flex',
      width: '100%',
      justifyContent: 'center',
    },
    titleContainer: {
      display: 'flex',
      width: '100%',
      position: 'fixed',
      zIndex: 100,
      paddingY: '1em',
      backgroundColor: '#E5E5E5',
    },
    mainTitleContainer: {
      marginTop: '5em',
      width: isMobile ? '95%' : '70%',
      justifyContent: 'flex-start',
    },
  };
  return (
    <Box id='poi-search-container' sx={styles.container}>
      <Box sx={styles.subContainer}>
        {isMobile ? (
          <Box sx={styles.titleContainer}>
            <Stack direction='row' spacing={1} sx={{ marginX: '0.3em' }}>
              <Typography
                component='h1'
                style={{ fontSize: '1.5em', fontFamily: 'Luciole-Bold' }}
              >
                {t('poiListing.menu-main-title')}
              </Typography>
              <GoBackMapButton />
            </Stack>
          </Box>
        ) : (
          <>
            <Box sx={{ position: 'absolute', top: '2em', right: 15 }}>
              <GoBackMapButton />
            </Box>
          </>
        )}

        <Stack direction={'column'} spacing={2} sx={styles.mainTitleContainer}>
          {!isMobile && (
            <Typography
              component='h1'
              style={{ fontSize: '2em', fontFamily: 'Luciole-Bold' }}
            >
              {t('poiListing.menu-main-title')}
            </Typography>
          )}

          <Stack direction='row' spacing={1} style={{ marginTop: '2em' }}>
            <FactCheckIcon sx={{ fontSize: isMobile ? '1.5rem' : '2rem' }} />
            <InputLabel
              id='menu-theme-title'
              role='presentation'
              style={{
                color: 'black',
                fontSize: isMobile ? '1em' : '1.5em',
                fontFamily: 'Luciole-Bold',
              }}
            >
              {t('poiListing.menu-theme-title')}
            </InputLabel>
          </Stack>
          <RadioButtons
            valueList={layerList}
            labelList={layerLabelList}
            labelledby='menu-theme-title'
            callbackFunction={(value) => setTheme(value)}
          />

          <Stack direction='row' spacing={1} style={{ marginTop: '2em' }}>
            <PinDropIcon sx={{ fontSize: isMobile ? '1.5rem' : '2rem' }} />
            <InputLabel
              htmlFor='search-poi'
              style={{
                color: 'black',
                fontSize: isMobile ? '1em' : '1.5em',
                fontFamily: 'Luciole-Bold',
              }}
            >
              {t('poiListing.menu-address-title')}
            </InputLabel>
          </Stack>
          <GeocoderInput
            id='search-poi'
            placeholder={t('poiListing.search-placeholder')}
            onComplete={(label, center) => setAddressCoords(center)}
            inputValue=''
          />

          <Stack direction='row' spacing={1} style={{ marginTop: '2em' }}>
            <StraightenIcon sx={{ fontSize: isMobile ? '1.5rem' : '2rem' }} />
            <InputLabel
              id='menu-distance-title'
              role='presentation'
              style={{
                color: 'black',
                fontSize: isMobile ? '1em' : '1.5em',
                fontFamily: 'Luciole-Bold',
              }}
            >
              {t('poiListing.menu-distance-title')}
            </InputLabel>
          </Stack>
          <RadioButtons
            valueList={distanceValues}
            labelList={distanceLabels}
            labelledby='menu-distance-title'
            callbackFunction={(value) => setDistance(value)}
          />

          {addressCoords.length > 0 && (
            <>
              <Stack direction='row' spacing={1} style={{ marginTop: '2em' }}>
                <PushPinIcon sx={{ fontSize: isMobile ? '1.5rem' : '2rem' }} />
                <Typography
                  component='h2'
                  style={{
                    fontSize: isMobile ? '1em' : '1.5em',
                    fontFamily: 'Luciole-Bold',
                  }}
                >
                  {t('poiListing.menu-listing-title')}
                </Typography>
              </Stack>
              <Box sx={{ paddingBottom: '2em' }}>
                {isLoading && <LinearProgress />}
                {!isLoading && (
                  <FeatureListingResult poiList={poiList} isMobile={isMobile} />
                )}
              </Box>
            </>
          )}
        </Stack>
      </Box>
    </Box>
  );
};
